<template>
	<div>
		<RouterLink
			class="drawer-item-chat drawer-item"
			draggable="false"
			:class="{
				[`chat-${chat.id}`]: true,
				'menu-open': $refs.menu?.$refs?.menu?.isOpen,
				active: isActive,
				visible: isVisible,
			}"
			:to="linkTo"
			role="button"
			tabindex="-1"
		>
			<div class="drawer-item-icon">
				<UserGroupIcon v-if="!chat.isDM" type="solid" />
				<ProfilePizza
					v-if="chat.isMultiDM"
					:userIds="dmMemberIds"
					:size="32"
					showTotal
					alphabetical
				/>
				<ProfilePic
					v-if="chat.isdm && (partner || selfDM)"
					:user="selfDM ? myUser : partner"
					enableOnline
				/>
			</div>

			<Tippy
				class="drawer-item-content"
				theme="large"
				:content="chat.isMultiDM ? chat.displayname : null"
				:options="{
					interactive: false,
					arrow: true,
					maxWidth: '200px',
					delay: [1000, 0],
				}"
			>
				<div
					v-if="category"
					class="drawer-item-category"
					v-text="category.displayname"
				/>
				<div class="drawer-item-title">
					<span
						class="drawer-item-title-text"
						:class="{ unread: hasUnread }"
						v-text="chat.displayname"
					/>
					<span v-if="chat.isdm && lastActiveTime" class="last-active"
						>&middot; {{ lastActiveTime }}</span
					>
					<BadgeCheckIcon
						v-if="isSupportDM"
						class="support-badge"
						type="solid"
					/>
				</div>
			</Tippy>
			<div class="drawer-item-actions">
				<BellSlashIcon type="solid" v-if="isMuted" class="muted-group-icon" />

				<EyeSlashIcon type="solid" v-if="isHidden" class="hidden-icon" />

				<UnreadBadge
					v-if="showBadge"
					class="drawer-item-badge"
					:class="{ 'non-mentions': !isMarkedUnread && !mentionsCount }"
					:unread="mentionsCount || unreadCount"
					showDot
				/>

				<div
					v-if="isLive"
					class="drawer-item-live"
					v-t="'meetings_live_livebutton'"
				/>

				<DotsMenu
					class="drawer-item-menu"
					localize
					:items="menuOptions"
					@select="onMenuItemClick"
					@keyup.enter="$refs.menu?.$refs?.menu?.toggleMenu()"
					ref="menu"
				/>
			</div>
		</RouterLink>

		<!-- HIDE MODAL -->
		<!-- TODO: move this modal to its own file and reuse within GroupOptions.vue -->
		<StandardModal
			v-if="showHideModal"
			small
			open
			:title="
				chat.isdm
					? $t('groupsettings_option_hidedm')
					: $t('chat_drawer_section_hide_group')
			"
			:confirmText="$t('groupsettings_option_hidedm')"
			@confirm="hideGroup"
			@cancel="showHideModal = false"
			:confirmProps="{ loading: isLoading }"
		>
			<div class="drawer-modal-content">
				<EyeSlashIcon halo />
				<div
					class="modal-title"
					v-text="
						chat.isdm
							? $t('chat_drawer_section_hide_messages', {
									UserName: partner.fullname,
							  })
							: $t('chat_drawer_section_hide_description_group')
					"
				/>
				<div
					v-if="chat.isdm"
					class="modal-text"
					v-t="'chat_drawer_section_hide_description_dm'"
				/>
			</div>
		</StandardModal>

		<StandardModal
			v-if="showMaxPinnedModal"
			small
			open
			:title="$t('common_max_limit')"
			:confirmText="$t('common_button_close')"
			@confirm="showMaxPinnedModal = false"
			@cancel="showMaxPinnedModal = false"
			:confirmProps="{ danger: true, loading: isLoading }"
		>
			<div class="drawer-modal-content max-pinned">
				<CircleExclamationIcon halo />
				<div
					class="modal-title"
					v-text="
						$t('chat_drawer_section_pinned_limit', { Count: MAX_PINNED_CHATS })
					"
				/>
			</div>
		</StandardModal>

		<NicknameModal
			v-if="showNicknameModal"
			:chatId="chat.id"
			@close="showNicknameModal = false"
		/>

		<LeaveModal
			v-if="showLeaveModal"
			:chatId="chat.id"
			@close="showLeaveModal = false"
		/>

		<MuteModal
			v-if="showMuteModal"
			:group="true"
			:bubble_id="chat.id"
			@close="showMuteModal = false"
		/>
	</div>
</template>

<script>
import store, {
	getVideosession,
	getUser,
	getCategory,
	getPinnedChats,
	updateStat,
	getStat,
	getMyMembershipForBubble,
	setMembership,
	getUsersForBubble,
	getMyUserId,
} from '@/store'
import {
	UnreadBadge,
	DotsMenu,
	StandardModal,
	ProfilePic,
	ProfilePizza,
	Tippy,
} from '@/components/common'
import { MAX_PINNED_CHATS } from '@/helpers/constants'
import {
	BellSlashIcon,
	EyeSlashIcon,
	CircleExclamationIcon,
	MessageCheckIcon,
	MessageDotIcon,
	ThumbtackSlashIcon,
	ThumbtackIcon,
	BellIcon,
	EyeIcon,
	PencilIcon,
	ArrowRightToBracketIcon,
	UserGroupIcon,
	BadgeCheckIcon,
} from '@/icons'
import { post } from '@/helpers/api'
import { redirectToLastActiveGroup } from '@/helperMethods'
import { closeNotificationsForBubble } from '@/helpers/pushNotification'
import NicknameModal from './NicknameModal'
import LeaveModal from './LeaveModal'
import MuteModal from '@/components/MuteModal'
import moment from 'moment'
import { getTimeAgoShorthand, sStorage, isDateISO } from '@/helpers/utils'
import { PINNED, RECENTS } from './SidebarCategory.vue'
import scrollIntoView from 'scroll-into-view-if-needed'
import { markChatAsRead, markChatAsUnread } from '@/api/membership'

export default {
	name: 'SidebarChat',
	props: {
		chat: Object,
		isPinned: Boolean,
		isRecent: Boolean,
		isVisible: Boolean,
	},
	components: {
		UnreadBadge,
		BellSlashIcon,
		EyeSlashIcon,
		DotsMenu,
		CircleExclamationIcon,
		StandardModal,
		NicknameModal,
		LeaveModal,
		MuteModal,
		ProfilePic,
		UserGroupIcon,
		ProfilePizza,
		Tippy,
		BadgeCheckIcon,
	},

	data() {
		return {
			partner: getUser(this.chat?.dmpartner_id),
			showHideModal: false,
			showNicknameModal: false,
			showLeaveModal: false,
			showMuteModal: false,
			showMaxPinnedModal: false,
			isLoading: false,
			MAX_PINNED_CHATS,
		}
	},

	mounted() {
		if (this.chat.dmpartner_id) {
			this.$onEventBus('user-update', () => {
				this.partner = getUser(this.chat?.dmpartner_id)
			})
		}
		this.routerUnsubscribe = this.$router.afterEach((to) => {
			// clear temp pronto_marked_unread flag if navigating away
			if (to.name !== 'chat' || to.params.id !== this.chat.id) {
				sStorage.remove(`pronto_marked_unread_${this.chat.id}`)
			}
		})
	},

	unmounted() {
		this.routerUnsubscribe?.()
	},

	computed: {
		category() {
			return this.canShowBadge ? getCategory(this.chat.category_id) : null
		},
		isActive() {
			const { name, path, params } = this.$route
			if (name !== 'chat' || parseInt(params.id) !== this.chat.id) return false
			if (this.isPinned && path.includes('pinned')) return true
			if (this.isRecent && path.includes('recent')) return true
			if (!this.isPinned && !this.isRecent && path.includes('chat')) return true
			return false
		},
		isLive() {
			if (this.isMuted || !this.canShowBadge) return false
			const session = getVideosession(this.chat.videosession_id)
			return session?.status == 'ACTIVE'
		},
		isMuted() {
			return this.chat.mute ?? false
		},
		isHidden() {
			return this.chat.ishidden ?? false
		},
		canShowBadge() {
			return this.isPinned || this.isRecent
		},
		unreadCount() {
			return this.chat.unread ?? 0
		},
		mentionsCount() {
			return this.chat.unread_mentions ?? 0
		},
		isMarkedUnread() {
			return this.chat.marked_unread
		},
		hasUnread() {
			return (
				this.canShowBadge &&
				(this.unreadCount > 0 || this.mentionsCount > 0 || this.isMarkedUnread)
			)
		},
		showBadge() {
			if (this.isLive || !this.canShowBadge) return false
			if (this.mentionsCount) return true
			if (this.isMarkedUnread) return true
			if (this.unreadCount && !this.isMuted) return true
			return false
		},
		isPartnerOnline() {
			return this.partner?.isonline ?? false
		},
		dmMemberIds() {
			if (!this.chat.isMultiDM) return []
			return getUsersForBubble(this.chat.id).map((u) => u.id)
		},
		lastActiveTime() {
			const { isonline, last_presence_at } = this.partner || {}
			if (!this.partner || isonline || !last_presence_at) return ''
			try {
				const isISO = isDateISO(last_presence_at)
				const presence = moment(
					isISO ? last_presence_at : last_presence_at + 'Z'
				)
				const hourCheck = moment.utc(store.today).diff(presence, 'hours')

				if (hourCheck > 24) return ''
				return getTimeAgoShorthand(presence)
			} catch (err) {
				console.error(err)
				return ''
			}
		},
		menuOptions() {
			const { is_pinned, isdm, grantleavegroup } = this.chat
			return [
				{
					// mark as read / unread
					id: this.hasUnread ? 'markasread' : 'markasunread',
					label: this.hasUnread
						? 'chat_drawer_action_markread'
						: 'chat_drawer_action_markunread',
					icon: this.hasUnread ? MessageCheckIcon : MessageDotIcon,
				},
				{
					// pin / unpin
					id: is_pinned ? 'unpin' : 'pin',
					label: is_pinned
						? 'chat_drawer_action_unpin'
						: 'chat_drawer_action_pin',
					icon: is_pinned ? ThumbtackSlashIcon : ThumbtackIcon,
				},
				{
					// mute / unmute
					id: this.isMuted ? 'unmute' : 'mute',
					label: this.isMuted
						? 'chat_drawer_action_unmute'
						: 'chat_drawer_action_mute',
					icon: this.isMuted ? BellIcon : BellSlashIcon,
					iconProps: {
						class: 'drawer-item-mute-icon',
					},
				},
				{
					// hide / unhide
					id: this.isHidden ? 'unhide' : 'hide',
					label: this.isHidden
						? 'chat_drawer_action_unhide'
						: 'chat_drawer_action_hide',
					icon: this.isHidden ? EyeSlashIcon : EyeIcon,
				},
				!isdm && {
					id: 'nickname',
					label: 'chat_drawer_action_nickname',
					icon: PencilIcon,
				},
				!isdm &&
					grantleavegroup && {
						id: 'leave',
						label: 'chat_drawer_action_leave',
						danger: true,
						icon: ArrowRightToBracketIcon,
					},
			]
				.filter((x) => x)
				.map((x) => {
					x.iconProps = x.iconProps || {}
					x.iconProps.type = x.iconProps.type || 'regular'
					return x
				})
		},
		linkTo() {
			const chat = this.isPinned ? PINNED : this.isRecent ? RECENTS : 'chat'
			return { name: 'chat', params: { id: this.chat.id, chat } }
		},
		isSupportDM() {
			if (!this.chat.is_dm || this.chat.isMultiDM) return false
			const supportMembership = getUsersForBubble(this.chat.id).find(
				(u) => u.isSupportUser && u.id !== getMyUserId()
			)
			return !!supportMembership
		},

		selfDM() {
			return this.chat.dm_memberships_count === 1 && this.chat.isdm
		},

		myUser() {
			return getUser(getMyUserId())
		},
	},
	methods: {
		onMenuItemClick({ id }) {
			switch (id) {
				case 'markasread':
					return this.markAsRead()
				case 'markasunread':
					return this.markAsUnread()
				case 'pin':
					return this.saveIsPinned(true)
				case 'unpin':
					return this.saveIsPinned(false)
				case 'hide':
					return this.onHideClick()
				case 'unhide':
					return this.unhideGroup()
				case 'nickname':
					this.showNicknameModal = true
					return
				case 'leave':
					this.showLeaveModal = true
					return
				case 'mute':
					this.showMuteModal = true
					return
				case 'unmute':
					return this.unmuteGroup()
			}
		},

		async markAsRead() {
			const { latest_message_id, mark, unread, unread_mentions } = getStat(
				this.chat.id
			)

			if (this.isMarkedUnread) {
				try {
					await markChatAsRead(this.chat.id)
				} catch (e) {
					this.$alert.error(e)
				}
			}
			sStorage.remove(`pronto_marked_unread_${this.chat.id}`)

			updateStat(this.chat.id, {
				unread: 0,
				unread_mentions: 0,
			})

			try {
				const { data } = await post('/api/v1/bubble.mark', {
					bubble_id: this.chat.id,
					message_id: latest_message_id,
				})
				if (data.ok) {
					updateStat(this.chat.id, {
						unread: 0,
						unread_mentions: 0,
						mark: latest_message_id,
					})

					closeNotificationsForBubble(this.chat.id)
				} else {
					updateStat(this.chat.id, {
						unread,
						unread_mentions,
						mark,
					})
				}
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
				updateStat(this.chat.id, {
					unread,
					unread_mentions,
				})
			}
		},

		async markAsUnread() {
			try {
				await markChatAsUnread(this.chat.id)
			} catch (e) {
				this.$alert.error(e)
			}
		},

		onHideClick() {
			// if first time this user has hidden, show confirm modal
			if (!localStorage.getItem('showHideConfirm')) {
				this.showHideModal = true
				localStorage.setItem('showHideConfirm', 'false')
				return
			}

			this.hideGroup()
		},

		async saveIsPinned(is_pinned) {
			if (is_pinned) {
				// check if user has hit the limit for pinned chats
				const pinnedCount = getPinnedChats().length
				if (pinnedCount >= MAX_PINNED_CHATS) {
					this.showMaxPinnedModal = true
					return
				}
			}

			this.isLoading = true

			// optimistically update our membership to be marked as is_pinned
			const membership = getMyMembershipForBubble(this.chat.id)
			membership.is_pinned = is_pinned
			setMembership(membership)

			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.chat.id,
					is_pinned,
				})
				setMembership(data.membership)

				if (is_pinned) {
					// scroll up to the pinned section
					EventBus.$emit('expand-category', 'pinned')
					const elem = document.querySelector(
						`.pinned .drawer-item-chat.chat-${this.chat.id}`
					)
					if (elem) {
						scrollIntoView(elem, {
							scrollMode: 'if-needed',
							block: 'center',
							inline: 'nearest',
							behavior: 'smooth',
						})
					}
				}
			} catch (e) {
				console.error(e)
				membership.is_pinned = !is_pinned
				setMembership(membership)
			}
			this.isLoading = false
		},

		async hideGroup() {
			this.isLoading = true

			// optimistically update our membership to be marked as hidden.
			const membership = getMyMembershipForBubble(this.chat.id)
			membership.ishidden = true
			setMembership(membership)

			const endpoint = this.chat.isdm
				? '/api/v1/bubble.leave'
				: '/api/v1/membership.update'

			try {
				const { data } = await post(endpoint, {
					bubble_id: this.chat.id,
					ishidden: 1,
				})
				EventBus.$emit('groupHidden')
				this.showHideModal = false
				setMembership(data.membership)
				if (this.isActive) {
					redirectToLastActiveGroup()
				}
			} catch (e) {
				console.error(e)
				//our group didn't hide for some reason update membership.ishidden to false
				this.$refs.hideModal?.$alert.error(
					this.$t('chat_drawer_section_error_hide', {
						GroupName: this.chat.title,
					})
				)

				membership.ishidden = false
				setMembership(membership)
			}
			this.isLoading = false
		},

		async unhideGroup() {
			const membership = getMyMembershipForBubble(this.chat.id)
			membership.ishidden = false
			setMembership(membership)

			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.chat.id,
					ishidden: 0,
				})
				if (!data.ok) {
					throw new Error(data.error || 'Failed to unhide group')
				}
			} catch (e) {
				console.error(e)
				membership.ishidden = true
				setMembership(membership)
				this.$alert.error(e)
			}
		},

		async unmuteGroup() {
			try {
				const { data } = await post('/api/v1/membership.update', {
					bubble_id: this.chat.id,
					mute: false,
					muteuntil: null,
				})
				setMembership(data.membership)
			} catch (e) {
				console.error(e)
				this.$alert.error(e)
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@use "sass:math";
@import 'src/sass/variables';

$item-height: 42px;
$gap: 8px;

.drawer-item-chat {
	position: relative;
	width: 100%;
	height: $item-height;
	padding: 0 8px 0 16px;
	@include flex($gap);
	color: inherit;
	text-decoration: none;
	&:hover {
		background: var(--neutral-200);
		.drawer-item-menu {
			width: 20px !important;
			opacity: 1 !important;
		}
		&:not(.active) .profile-pic-avatar {
			:deep(.online-indicator) {
				border-color: var(--neutral-200) !important;
			}
		}
	}
	&:focus {
		background: var(--neutral-200);
		.drawer-item-menu {
			width: 20px !important;
			opacity: 1 !important;
		}
	}
	&.menu-open {
		background: var(--neutral-200);
		.drawer-item-menu {
			width: 20px !important;
			opacity: 1 !important;
			:deep(.actions) {
				background: var(--neutral-300) !important;
			}
		}
		&.active {
			:deep(.actions) {
				background: var(--teal-200) !important;
			}
		}
	}
	&.active {
		background: var(--teal-300);
		.pronto-icon {
			color: var(--neutral-0) !important;
		}
		.drawer-item-content,
		.drawer-item-category {
			color: var(--neutral-0) !important;
		}
		.drawer-item-actions {
			.drawer-item-menu {
				:deep(.pronto-icon) {
					color: var(--neutral-0) !important;
				}
				:deep(.actions) {
					&:hover {
						background: var(--teal-200) !important;
					}
				}
			}
		}
		.drawer-item-content {
			.drawer-item-title {
				.last-active {
					color: var(--neutral-0);
				}
			}
		}
		.drawer-item-icon {
			> .profile-pizza,
			> .profile-pic-avatar {
				border-color: transparent;
			}
			.profile-pic-avatar {
				:deep(.online-indicator) {
					background-color: white;
					border-color: var(--teal-300);
				}
			}
		}
	}
	.drawer-item-icon {
		flex: 0 0 32px;
		width: 32px;
		@include flex-center;
		> .pronto-icon {
			font-size: 20px;
			color: var(--neutral-500);
		}
		:deep(.online-indicator) {
			border-color: $drawer-gray;
		}
	}
	.drawer-item-content {
		flex: 1 1 0%;
		min-width: 0;
		@include flex-column;
		overflow: hidden;
		font-size: 15px;
		color: var(--neutral-800);

		.drawer-item-category {
			text-transform: uppercase;
			color: var(--neutral-500);
			font-size: 8px;
			letter-spacing: 1px;
			@include ellipsis;
		}
		.drawer-item-title {
			@include flex($gap);
			width: max-content;
			max-width: 100%;
			.profile-pic-avatar,
			.profile-pizza {
				flex-shrink: 0;
				:deep(.online-indicator) {
					border-color: $drawer-gray;
				}
			}
			.last-active {
				@include fontFamily(500);
				flex-shrink: 0;
				color: var(--neutral-300);
			}
			.drawer-item-title-text {
				@include ellipsis;
				&.unread {
					@include fontFamily(900);
				}
			}
			.support-badge {
				color: var(--blue-300);
			}
		}
	}
	.drawer-item-actions {
		flex: 0 0 auto;
		@include flex($gap);
		margin-left: auto;
		.pronto-icon {
			color: var(--neutral-500);
		}
		:deep(.pronto-icon) {
			color: var(--neutral-500);
		}
		.drawer-item-badge {
			&.non-mentions {
				color: var(--neutral-500);
				background: var(--neutral-0);
			}
		}
		.drawer-item-menu {
			width: 0;
			opacity: 0;
			overflow: hidden;
			transition: all ease-in-out 100ms;
			transition-property: width, opacity;
			:deep(.actions) {
				width: 20px;
				height: 25px;
				overflow: hidden;
				padding: 0;
				border-radius: 3px;
				font-size: 18px;
				&:hover {
					background: var(--neutral-300) !important;
				}
			}
			&:focus {
				width: 20px !important;
				opacity: 1 !important;
			}
		}
		.drawer-item-live {
			background-color: var(--red-400);
			@include fontFamily(900);
			font-size: 12px;
			color: white;
			padding: 2px 4px;
		}
	}
}

.drawer-modal-content {
	@include flex-column-center;
	justify-content: space-around;
	height: 100%;
	padding: 16px 40px;
	text-align: center;
	&.max-pinned {
		.pronto-icon {
			color: var(--red-200);
		}
	}
	.pronto-icon {
		font-size: 45px;
		color: var(--blue-300);
		margin-bottom: 16px;
	}
	.modal-title {
		@include fontFamily(500);
		margin-bottom: 8px;
	}
	.modal-text {
		@include fontFamily(500);
		font-size: 14px;
		color: var(--neutral-500);
	}
}
</style>

<style lang="scss">
.drawer-item-mute-icon {
	font-size: 16px !important;
	margin-left: -2px;
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.drawer-item-chat {
		&.active {
			background: var(--neutral-300) !important;
			.drawer-item-content,
			.drawer-item-category {
				color: white !important;
			}
			.drawer-item-content {
				.drawer-item-title {
					.last-active {
						color: var(--neutral-800) !important;
					}
					.support-badge {
						color: var(--blue-300) !important;
					}
				}
			}
			.drawer-item-actions {
				.drawer-item-menu {
					.pronto-icon {
						color: var(--neutral-800) !important;
					}
					.actions {
						background: var(--neutral-0) !important;
						&:hover {
							background: var(--neutral-0) !important;
						}
					}
				}
			}
			.drawer-item-icon {
				.pronto-icon {
					color: var(--neutral-800) !important;
				}
				.online-indicator {
					background-color: var(--teal-300) !important;
					border-color: var(--neutral-300) !important;
				}
			}
		}
		.drawer-item-actions {
			.drawer-item-badge {
				&.non-mentions {
					color: var(--neutral-800) !important;
					background: var(--neutral-500) !important;
				}
			}
			.drawer-item-live {
				color: var(--neutral-800);
			}
		}
		.drawer-item-content {
			.drawer-item-title {
				.last-active {
					color: var(--neutral-400) !important;
				}
			}
		}
		.drawer-item-icon {
			.online-indicator {
				border-color: var(--neutral-0) !important;
			}
		}
	}
}
</style>
